
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Account, LeadAccount, StrategyWithDetails } from "@/types/strategy-types";
import { FormActions } from "@/components/admin/strategy-form/FormActions";
import { Form } from "@/components/ui/form";
import AccountBasicFields from "./fields/AccountBasicFields";
import AccountStrategyFields from "./fields/AccountStrategyFields";
import AccountPerformanceField from "./fields/AccountPerformanceField";
import AccountToggleFields from "./fields/AccountToggleFields";
import { useAccountFiltering } from "@/hooks/admin/accounts/useAccountFiltering";

const accountSchema = z.object({
  external_id: z.string().min(1, { message: "External ID is required" }),
  exchange: z.string().min(1, { message: "Exchange is required" }),
  strategy_id: z.string().min(1, { message: "Strategy is required" }),
  lead_account_id: z.string().min(1, { message: "Lead account is required" }),
  is_primary: z.boolean().default(false),
  active: z.boolean().default(true),
  performance_url: z.string().optional(),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface AccountFormProps {
  onSubmit: (account: Partial<Account>) => Promise<void>;
  initialData?: Account;
  leadAccounts: LeadAccount[];
  strategies: StrategyWithDetails[];
  onCancel: () => void;
  userRole?: string | null;
}

const AccountForm = ({
  onSubmit,
  initialData,
  leadAccounts,
  strategies,
  onCancel,
  userRole
}: AccountFormProps) => {
  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: initialData ? {
      ...initialData,
      is_primary: initialData.is_primary || false,
      active: initialData.active !== false,
      performance_url: initialData.performance_url || "",
    } : {
      active: true,
      is_primary: false,
      performance_url: "",
    }
  });

  // Use the custom hook for filtering strategies and lead accounts
  const { filteredStrategies, filteredLeadAccounts } = useAccountFiltering(
    userRole,
    strategies,
    leadAccounts
  );

  const handleSubmit = async (values: AccountFormData) => {
    try {
      await onSubmit(values);
      onCancel();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <AccountBasicFields form={form} />
        
        <AccountStrategyFields 
          form={form} 
          filteredStrategies={filteredStrategies} 
          filteredLeadAccounts={filteredLeadAccounts} 
        />
        
        <AccountPerformanceField form={form} />
        
        <AccountToggleFields form={form} />

        <FormActions
          onCancel={onCancel}
          isSubmitting={form.formState.isSubmitting}
          isEdit={!!initialData}
        />
      </form>
    </Form>
  );
};

export default AccountForm;
